/**
 * useAssociationLookup — Issue #344.
 *
 * Hook that powers shared admin-web association lookup selectors. Backed by
 * `@jinzhuan/api-client` real endpoints; no frontend filtering of cached
 * lists, no mock data.
 *
 * Red lines:
 *   - MUST use the typed api-client; never hand-write fetch.
 *   - Debounce keystrokes so the network is not hammered.
 *   - Surface explicit loading / empty / error states; the selector renders
 *     each one distinctly so users know whether the empty list means "no
 *     match" or "request failed".
 *   - Never log/echo response items elsewhere — the items returned here
 *     contain a display name (PII-sensitive); they belong only inside the
 *     selector's local UI.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

/**
 * Lookup kinds supported by the shared admin selector. `user` and `employee`
 * are intentionally distinct ID domains:
 *   - `user`     → returns `users.id` (system login account). Use this for
 *                  ownerUserId / assignedUserId / assigneeId / ownerId fields
 *                  that the backend persists as a system user reference.
 *   - `employee` → returns `hr_employees.id` (HR roster). Use this only for
 *                  HR-specific surfaces; never mix into user-owned fields.
 */
export type AssociationLookupKind =
  | 'customer'
  | 'user'
  | 'employee'
  | 'channel_partner';

export type AssociationLookupItem = ApiComponents['schemas']['AssociationLookupItemDto'];

interface UseAssociationLookupOptions {
  /** Which lookup endpoint to call. */
  kind: AssociationLookupKind;
  /** User-entered keyword (raw, undebounced). */
  query: string;
  /** Whether the parent UI is currently open. When false, the hook stays idle. */
  enabled: boolean;
  /** Optional override for the suggestion cap. Server clamps to [1, 20]. */
  limit?: number;
  /** Debounce interval in ms. Defaults to 250. */
  debounceMs?: number;
}

export interface UseAssociationLookupResult {
  items: readonly AssociationLookupItem[];
  loading: boolean;
  error: string | null;
}

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

const KIND_PATHS: Record<
  AssociationLookupKind,
  | '/api/v1/association-lookup/customers'
  | '/api/v1/association-lookup/users'
  | '/api/v1/association-lookup/employees'
  | '/api/v1/association-lookup/channel-partners'
> = {
  customer: '/api/v1/association-lookup/customers',
  user: '/api/v1/association-lookup/users',
  employee: '/api/v1/association-lookup/employees',
  channel_partner: '/api/v1/association-lookup/channel-partners',
};

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return '会话已过期，请重新登录';
    if (err.status === 403) return '当前账号无权限检索此关联对象';
    return err.message || `请求失败（HTTP ${err.status}）`;
  }
  if (err instanceof Error) return err.message;
  return '请求失败，请稍后重试';
}

export function useAssociationLookup(
  options: UseAssociationLookupOptions,
): UseAssociationLookupResult {
  const { kind, query, enabled, limit, debounceMs = 250 } = options;
  const [items, setItems] = useState<readonly AssociationLookupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const path = KIND_PATHS[kind];

  const run = useCallback(
    async (signal: AbortSignal, q: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params: { q?: string; limit?: number } = {};
        if (q.trim().length > 0) params.q = q.trim();
        if (limit !== undefined) params.limit = limit;
        const { data, error: opError } = await apiClient.GET(path, {
          params: { query: params },
          signal,
        });
        if (signal.aborted) return;
        if (opError) {
          setError(toErrorMessage(opError));
          setItems([]);
          return;
        }
        if (data?.items) setItems(data.items);
        else setItems([]);
      } catch (err) {
        if (signal.aborted) return;
        setError(toErrorMessage(err));
        setItems([]);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [path, limit],
  );

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setError(null);
      setLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      void run(controller.signal, query);
    }, debounceMs);

    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, query, debounceMs, run]);

  useEffect(() => {
    return (): void => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return useMemo(() => ({ items, loading, error }), [items, loading, error]);
}
